import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getRuntimeCapabilities, type RuntimeCapabilities } from '../../../shared/api/runtime/capabilities';
import { webLog } from '../../../shared/api/grpc/webLogger';
import { snapshotCurrentPcfSettingsStoreForDebug } from '../../../shared/api/pipeex/pcfSettingsClient';
import {
  registerRevitModelCacheInvalidator,
  type RevitModelInvalidation,
} from '../../../shared/lib/revit/modelCacheInvalidation';
import { useActiveDocumentInfoContext, useActiveDocumentRefresh } from '../../../shared/hooks/ActiveDocumentProvider';
import { useRevitApi } from '../../../shared/api/revit/useRevitApi';
import { usePcfValidationStore } from '../../../shared/stores/usePcfValidationStore';
import { MOCK_REPORT } from '../lib/validationMockData';
import { applyValidationMetadata, formatMs, nowMs } from '../lib/validationStateHelpers';
import {
  ValidationRunCoordinator,
  type ValidationRunCoordinator as ValidationRunCoordinatorType,
} from '../lib/validationRunCoordinator';
import { describeValidationContext } from '../lib/validationContextFingerprint';
import { filterSelectableValidationIds } from '../lib/validationReportNormalizer';
import { buildValidationMappingOverridesFromStore } from '../lib/validationMappingOverrides';
import {
  getSelectableMockValidations,
  loadDocumentInfo,
  loadLastReport,
  loadValidationSettings,
  loadValidations,
} from '../lib/validationDataLoaders';
import { USE_E2E_VALIDATION_MOCK, USE_GRPC, USE_MOCK } from '../lib/validationRuntimeEnv';
import { useValidationConfigSelection } from './useValidationConfigSelection';
import type { PcfValidationReport, ValidationDefinition } from '../types';
import type { ValidationRuntimeSettings } from '../lib/validationRuntimeSettings';

export interface UseValidationBootstrapOptions {
  setValidationSettings: (updater: (current: ValidationRuntimeSettings) => ValidationRuntimeSettings) => void;
  coordinator?: ValidationRunCoordinatorType<PcfValidationReport>;
  /**
   * Opaque token naming the verified setup revision the app currently sits in,
   * or `null` while nothing is verified. A setup revision opening, a connector
   * mutation settling, and a verified profile change all produce a new token,
   * so a single change of this value is the one signal that every cached run
   * and the visible report predate the setup that is live now.
   */
  verifiedRevision?: string | null;
  /** Margin the seeded mock report is stamped with; currency compares it. */
  reconnectGapMarginMillimeters?: number;
}

export interface UseValidationBootstrapReturn {
  runtimeCapabilities: RuntimeCapabilities;
  isReady: boolean;
  hasCheckedWebView: boolean;
  isWebView2: boolean;
  report: PcfValidationReport | null;
  validations: ValidationDefinition[];
  selectedValidations: number[];
  setSelectedValidations: React.Dispatch<React.SetStateAction<number[]>>;
  documentInfo: any;
  documentInfoChecked: boolean;
  setDocumentInfo: React.Dispatch<React.SetStateAction<any>>;
  setDocumentInfoChecked: React.Dispatch<React.SetStateAction<boolean>>;
  handleToggleValidation: (id: number) => void;
  handleSelectAll: () => void;
  handleDeselectAll: () => void;
  commitReport: (
    nextReport: PcfValidationReport,
    source: string,
    contextFingerprint?: string | null
  ) => void;
  coordinator: ValidationRunCoordinatorType<PcfValidationReport>;
}

export function useValidationBootstrap(
  options: UseValidationBootstrapOptions
): UseValidationBootstrapReturn {
  const { setValidationSettings, verifiedRevision = null, reconnectGapMarginMillimeters } = options;
  const { isReady: revitReady, call } = useRevitApi();
  const isReady = USE_MOCK || USE_GRPC || revitReady;
  const hasCheckedWebView = true;
  const runtimeCapabilities = useMemo(() => getRuntimeCapabilities(), []);
  const activeDocumentInfo = useActiveDocumentInfoContext();

  const report = usePcfValidationStore((state) => state.report);
  const setReport = usePcfValidationStore((state) => state.setReport);
  const markReportStale = usePcfValidationStore((state) => state.markReportStale);

  const [validations, setValidations] = useState<ValidationDefinition[]>(() =>
    USE_MOCK ? getSelectableMockValidations() : []
  );
  const [documentInfo, setDocumentInfo] = useState<any>(
    USE_MOCK ? { DocumentName: MOCK_REPORT.DocumentName } : null
  );
  const [documentInfoChecked, setDocumentInfoChecked] = useState(USE_MOCK);

  const {
    selectedValidations,
    setSelectedValidations,
    handleToggleValidation,
    handleSelectAll,
    handleDeselectAll,
  } = useValidationConfigSelection(validations);

  const coordinatorRef = useRef(
    options.coordinator ?? new ValidationRunCoordinator<PcfValidationReport>()
  );

  const commitReport = useCallback(
    (
      nextReport: PcfValidationReport,
      source: string,
      contextFingerprint: string | null = null
    ) => {
      const startedAt = nowMs();
      setReport(nextReport, contextFingerprint);
      const elapsedMs = nowMs() - startedAt;
      const message =
        `${source}: setReportMs=${formatMs(elapsedMs)} checks=${nextReport.TotalChecks ?? 0} issues=${nextReport.TotalIssues ?? 0} persist=metadata-only`;

      if (elapsedMs > 50) {
        webLog.warn('validation-performance', message);
      } else {
        webLog.info('validation-performance', message);
      }
    },
    [setReport]
  );

  /** Evidence captured under the previous setup can no longer be trusted. */
  const invalidateVisibleReport = useCallback(
    (event?: RevitModelInvalidation) => {
      // Validation-repair writes (quick fix apply/undo) are the feature's own
      // doing and are followed by an explicit scoped re-run; wiping the run
      // cache and stale-marking here would only force a full re-validation.
      // Under the manual-refresh gate a repair ModelChanged no longer reaches
      // this invalidator — it parks a pending version instead — so this flag
      // is only seen when an explicit refresh re-publishes the parked event.
      if (event?.isValidationRepair === true) {
        return;
      }

      coordinatorRef.current.invalidateAll();
      markReportStale();
    },
    [markReportStale]
  );

  useEffect(
    () => registerRevitModelCacheInvalidator(invalidateVisibleReport),
    [invalidateVisibleReport]
  );

  const { modelDataStale } = useActiveDocumentRefresh();

  // ModelChanged events only park a pending model version now; a parked change
  // means the visible report predates the live model. The pending-version
  // signal is a bare boolean — it loses the payload's isValidationRepair flag,
  // so a repair-parked version also lands here. That is accepted on purpose:
  // the scoped re-run that follows every repair commits a fresh report, which
  // clears the stale mark again.
  useEffect(() => {
    if (modelDataStale) {
      markReportStale();
    }
  }, [modelDataStale, markReportStale]);

  // The revision the hook mounted with is the one the visible report belongs
  // to, so only a *change* of the token means the setup moved underneath it.
  const lastVerifiedRevisionRef = useRef(verifiedRevision);
  useEffect(() => {
    if (lastVerifiedRevisionRef.current === verifiedRevision) {
      return;
    }

    lastVerifiedRevisionRef.current = verifiedRevision;
    invalidateVisibleReport();
  }, [verifiedRevision, invalidateVisibleReport]);

  // Seed the e2e mock report as *current*: stamp it with the fingerprint the
  // currency check rebuilds, so a freshly seeded lane presents its report instead
  // of the stale gate. A real report that predates the live setup still arrives
  // without a matching fingerprint and is treated as stale.
  useEffect(() => {
    if (!USE_E2E_VALIDATION_MOCK || report) {
      return;
    }

    const seededReport = applyValidationMetadata(MOCK_REPORT, {
      lastValidationDate: MOCK_REPORT.ValidationDate,
    });
    const { fingerprint } = describeValidationContext({
      activeProjectKey: activeDocumentInfo.projectKey ?? null,
      documentName: documentInfo?.DocumentName ?? null,
      documentPath: documentInfo?.DocumentPath ?? null,
      verifiedRevision,
      selectedValidationIds: filterSelectableValidationIds(selectedValidations, validations),
      settingsSnapshot: { ...snapshotCurrentPcfSettingsStoreForDebug() },
      reconnectGapMarginMillimeters: reconnectGapMarginMillimeters ?? 20,
      mappingOverrides: buildValidationMappingOverridesFromStore(),
    });
    setReport(seededReport, fingerprint);
  }, [
    report,
    setReport,
    activeDocumentInfo.projectKey,
    activeDocumentInfo.modelVersion,
    documentInfo,
    verifiedRevision,
    selectedValidations,
    validations,
    reconnectGapMarginMillimeters,
  ]);

  // Load validations and document info when ready.
  // Run SEQUENTIALLY, not as a concurrent burst: in the Revit plugin WebView2
  // the gRPC-web transport drops a simultaneous fan-out of fetches right at
  // panel startup - a single call reaches the server (200 OK) while the others
  // fail client-side with "Element not found (0x80070490)" before any HTTP
  // request leaves the WebView. Awaiting them one-by-one keeps the page from
  // wedging on the empty "No Open Documents" / failed-load state.
  useEffect(() => {
    if (USE_GRPC || (!USE_MOCK && revitReady)) {
      void (async () => {
        webLog.info(
          'validation.page-load',
          `page enter: starting load sequence (USE_GRPC=${USE_GRPC} revitReady=${revitReady})`
        );
        const startedAt = typeof performance !== 'undefined' ? performance.now() : 0;
        try {
          await loadValidations({ call, setValidations, setSelectedValidations });
          await loadDocumentInfo({
            call,
            setDocumentInfo,
            onComplete: () => setDocumentInfoChecked(true),
          });
          await loadValidationSettings({ call, report, setValidationSettings });
          // A session report that already carries a context fingerprint is
          // fresher evidence than the host's last-report snapshot, which can
          // only ever arrive fingerprintless (hence stale). Don't clobber it.
          const storedReportContext = usePcfValidationStore.getState();
          if (!storedReportContext.report || !storedReportContext.reportContextFingerprint) {
            await loadLastReport({
              call,
              commitReport,
              setValidationSettings,
              setDocumentInfo,
            });
          }
          const took = startedAt ? Math.round(performance.now() - startedAt) : -1;
          webLog.info('validation.page-load', `page enter: load sequence complete tookMs=${took}`);
        } catch (err) {
          webLog.error(
            'validation.page-load',
            `page enter: load sequence threw ${err instanceof Error ? err.message : String(err)}`
          );
        }
      })();
    } else {
      webLog.info(
        'validation.page-load',
        `page enter: load gated (USE_GRPC=${USE_GRPC} USE_MOCK=${USE_MOCK} revitReady=${revitReady})`
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revitReady]);

  void activeDocumentInfo;

  return {
    runtimeCapabilities,
    isReady,
    hasCheckedWebView,
    // "Running inside the Revit host" — now signalled purely by the gRPC runtime
    // (the WebView2 COM detection has been removed; WebView2 is just the shell).
    isWebView2: USE_MOCK || USE_GRPC,
    report,
    validations,
    selectedValidations,
    setSelectedValidations,
    documentInfo,
    documentInfoChecked,
    setDocumentInfo,
    setDocumentInfoChecked,
    handleToggleValidation,
    handleSelectAll,
    handleDeselectAll,
    commitReport,
    coordinator: coordinatorRef.current,
  };
}
